import * as Sentry from '@sentry/react';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
    TOP_TAGS_REQUESTED,
    TOP_CONTRIBUTORS_REQUESTED,
    IMPACT_FILTER_REQUESTED,
    SPECIFIC_CAMPAIGN_REQUESTED,
    CONNECTED_CAMPAIGN_REQUESTED,
} from './constants';
import {
    topTagsSucceeded,
    topTagsFailed,
    topContributorsSucceeded,
    topContributorsFailed,
    impactFilterSucceeded,
    impactFilterFailed,
    challengeSucceeded,
    challenegeFailed,
    connectedChallengeSucceeded,
    connectedChallenegeFailed,
} from './actions';
import {
    getChallengeTopTags,
    getChallengeTopContributors,
    getChallengeLitters,
    getCampaignById,
    getCampaignChallenges,
    getMLTags,
} from '../../api';
import {
    getTaxonomy,
    getSelectDate,
    getChallengeId,
    getSelectedFilter,
} from './selectors';
import { getUserData } from '../auth/selectors';
import { parseClientError } from '../../api/utils';
import { logException } from '../../logger';

// triggers when user `TOP_CONTRIBUTORS_REQUESTED` action is dispatched
function* fetchTopContributors() {
    try {
        let params = {};
        const date = yield select(getSelectDate);
        const challengeId = yield select(getChallengeId);
        params = { challengeId, params: { ...date } };
        const topContributors = yield call(getChallengeTopContributors, params);
        yield put(topContributorsSucceeded(topContributors));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(topContributorsFailed(message));
    }
}

// triggers when user `TOP_TAGS_REQUESTED` action is dispatched
function* fetchTopTags() {
    try {
        let params = {};
        const challengeId = yield select(getChallengeId);
        const taxonomy = yield select(getTaxonomy);
        params = { challengeId, params: { taxonomy } };
        // fetch both user and ml tags
        const [userTags, mlTags] = yield all([
            yield call(getChallengeTopTags, params),
            yield call(getMLTags, { taxonomy, challengeId }),
        ]);

        // combine result to update in state
        const result = {
            totalTags:
                (userTags.total ? userTags.total : 0) +
                (mlTags.total ? mlTags.total : 0),
            totalMlTags: mlTags.total ? mlTags.total : 0,
            totalDistilledTags: userTags.total ? userTags.total : 0,
            topTags: {
                userTags: userTags.tags ? userTags.tags : [],
                mlTags: mlTags.tags ? mlTags.tags : [],
            },
        };
        const userData = yield select(getUserData);
        logException('Campaign details to sentry', {
            ...userData,
            challengeId,
            taxonomy,
        });

        yield put(topTagsSucceeded(result));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(topTagsFailed(message));
    }
}

// triggers when user `IMPACT_FILTER_REQUESTED` action is dispatched
function* filterLitters() {
    const taxonomy = yield select(getSelectedFilter);
    try {
        let params = {};
        const challengeId = yield select(getChallengeId);
        params.challengeId = challengeId;
        if (taxonomy) {
            params.params = { taxonomy };
        }
        const litters = yield call(getChallengeLitters, params);
        yield put(impactFilterSucceeded(litters));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(impactFilterFailed(message));
    }
}

// triggers when 'SPECIFIC_CAMPAIGN_REQUESTED' action is dispatched
function* fetchCampaign() {
    try {
        const challengeId = yield select(getChallengeId);
        const challenge = yield call(getCampaignById, { challengeId });
        yield put(challengeSucceeded(challenge));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(challenegeFailed(message));
    }
}

function* fetchConnectedChallenges() {
    try {
        const challengeId = yield select(getChallengeId);
        const challenges = yield call(getCampaignChallenges, { challengeId });
        yield put(connectedChallengeSucceeded(challenges));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(connectedChallenegeFailed(message));
    }
}

export default function*() {
    yield all([
        yield takeLatest(TOP_TAGS_REQUESTED, fetchTopTags),
        yield takeLatest(TOP_CONTRIBUTORS_REQUESTED, fetchTopContributors),
        yield takeLatest(IMPACT_FILTER_REQUESTED, filterLitters),
        yield takeLatest(SPECIFIC_CAMPAIGN_REQUESTED, fetchCampaign),
        yield takeLatest(
            CONNECTED_CAMPAIGN_REQUESTED,
            fetchConnectedChallenges,
        ),
    ]);
}
